import { supabase } from '../../../shared/lib/supabase';

// Define the TournamentAccess interface based on your sample data
export interface TournamentAccess {
  id: string;
  tournament_id: string;
  organisation_id: string;
  access_type: string;
  granted_by: string | null;
  granted_at: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Joined fields
  tournament_name?: string;
  organisation_name?: string;
}

export interface TournamentAccessCreateInput {
  tournament_id: string;
  organisation_id: string;
  access_type: string;
  granted_by?: string | null;
  granted_at: string;
  is_active: boolean;
}

export interface TournamentAccessUpdateInput {
  tournament_id?: string;
  access_type?: string;
  granted_by?: string | null;
  granted_at?: string;
  is_active?: boolean;
}

class TournamentAccessService {
  async getAll(): Promise<TournamentAccess[]> {
    try {
      console.log('🔍 Fetching tournament access with joins...');

      const { data, error } = await supabase
        .from('tournament_access')
        .select(`
          *,
          tournaments!tournament_access_tournament_id_fkey(name),
          organisations!tournament_access_organisation_id_fkey(name)
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching tournament access:', error);
        throw error;
      }

      // Transform the data to include joined fields
      const transformedData = data?.map(item => ({
        ...item,
        tournament_name: item.tournaments?.name || '',
        organisation_name: item.organisations?.name || ''
      })) || [];

      console.log('✅ Tournament access fetched successfully:', transformedData.length, 'records');
      return transformedData;
    } catch (error) {
      console.error('❌ Tournament access fetch error:', error);
      throw error;
    }
  }

  async getById(id: string): Promise<TournamentAccess | null> {
    try {
      console.log(`🔍 Fetching tournament access with ID: ${id}`);

      const { data, error } = await supabase
        .from('tournament_access')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('❌ Error fetching tournament access by ID:', error);
        throw error;
      }

      console.log('✅ Tournament access fetched successfully:', data);
      return data;
    } catch (error) {
      console.error('❌ Tournament access fetch by ID error:', error);
      throw error;
    }
  }

  async create(input: TournamentAccessCreateInput): Promise<TournamentAccess> {
    try {
      console.log('📝 Creating tournament access:', input);

      const { data: result, error } = await supabase
        .from('tournament_access')
        .insert(input)
        .select()
        .single();

      if (error) {
        console.error('❌ Error creating tournament access:', error);
        throw error;
      }

      console.log('✅ Tournament access created successfully:', result);
      return result;
    } catch (error) {
      console.error('❌ Tournament access creation error:', error);
      throw error;
    }
  }

  async update(id: string, data: TournamentAccessUpdateInput): Promise<TournamentAccess> {
    try {
      console.log(`📝 Updating tournament access ${id}:`, data);

      const dataWithTimestamp = {
        ...data,
        updated_at: new Date().toISOString()
      };

      const { data: result, error } = await supabase
        .from('tournament_access')
        .update(dataWithTimestamp)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('❌ Error updating tournament access:', error);
        throw error;
      }

      console.log('✅ Tournament access updated successfully:', result);
      return result;
    } catch (error) {
      console.error('❌ Tournament access update error:', error);
      throw error;
    }
  }

  async delete(id: string): Promise<void> {
    try {
      console.log(`🗑️ Deleting tournament access: ${id}`);

      const { error } = await supabase
        .from('tournament_access')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('❌ Error deleting tournament access:', error);
        throw error;
      }

      console.log('✅ Tournament access deleted successfully');
    } catch (error) {
      console.error('❌ Tournament access deletion error:', error);
      throw error;
    }
  }
}

export const tournamentAccessService = new TournamentAccessService();
